import { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { getHistory } from "@/lib/procureApi";
import { STAGE, fmtDate, fmtInt } from "@/lib/format";
import { ChartLineUp } from "@phosphor-icons/react";

const DarkTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-sm border border-[#CAD1DB] bg-[#FFFFFF] px-3 py-2 text-xs">
      <div className="mb-1 font-mono text-[#64748B]">{fmtDate(label)}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-4 font-mono">
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2 w-2" style={{ background: p.color }} />
            {p.name}
          </span>
          <span className="text-[#0F172A]">{fmtInt(p.value)}d</span>
        </div>
      ))}
    </div>
  );
};

export const TurnaroundHistory = ({ buyer, refreshKey }) => {
  const [data, setData] = useState([]);

  useEffect(() => {
    getHistory(buyer).then((r) => setData(r.history || []));
  }, [buyer, refreshKey]);

  const enough = data.length >= 2;

  return (
    <div className="col-span-12 border border-[#E3E7EC] bg-[#FFFFFF] p-5 lg:col-span-8">
      <div className="mb-4 flex items-center gap-2">
        <ChartLineUp size={18} weight="duotone" className="text-[#F0323E]" />
        <div>
          <h3 className="text-sm font-semibold text-[#0F172A]">
            Turnaround History {buyer ? <span className="text-[#64748B]">· {buyer}</span> : ""}
          </h3>
          <p className="pf-label mt-0.5">Avg days open per snapshot · week-over-week · overall + per stage</p>
        </div>
      </div>
      {!enough ? (
        <div className="flex h-40 items-center justify-center rounded-sm border border-dashed border-[#CAD1DB] text-center text-sm text-[#64748B]">
          Upload the report on another day to compare turnaround over time.<br />
          <span className="text-[#9CA3AF]">One snapshot loaded so far.</span>
        </div>
      ) : (
        <div className="h-64" data-testid="turnaround-history-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 5, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#E3E7EC" vertical={false} />
              <XAxis dataKey="reference_date" tickFormatter={fmtDate} tick={{ fill: "#64748B", fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={fmtInt} tick={{ fill: "#64748B", fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={false} tickLine={false} width={44} />
              <Tooltip content={<DarkTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12, fontFamily: "IBM Plex Mono" }} />
              <Line type="monotone" dataKey="avg_turnaround" name="Overall" stroke="#0F172A" strokeWidth={2.5} dot={{ r: 3 }} />
              {Object.entries(STAGE).map(([k, v]) => (
                <Line key={k} type="monotone" dataKey={k} name={k} stroke={v.color} strokeWidth={1.5} dot={{ r: 2 }} strokeDasharray="4 3" />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};
